import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormily } from "../../../../hooks/useFormily";
import { Field, FormConsumer, FormProvider } from "@formily/react";
import { OffsetInput } from "../../../../components/fields/formily/NumberInput";
import { Modal } from "../../../../components/core/basic/Modal";
import { Button } from "../../../../components/core/basic/Button";
import { VStack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import { useUpdateProductOffsets } from "../../api/useUpdateProductOffsets";
import { OffsetDisplay } from "../../../../components/fields/base/NumberInput";
import { type Offset } from "../../../../zodios/apiDefinitions/pricing";

interface Props {
  planId: number;
  productData: {
    id?: number;
    product_id?: number;
    sku: string;
    asin?: string;
    offset_amount: number;
    offset_type: Offset["type"];
  }[];
}

export const SetProductPriceOffsetModal = NiceModal.create(({ planId, productData }: Props) => {
  const { t } = useTranslation(["pricingv2", "common"]);
  const modal = useModal();
  const updateOffsetMutation = useUpdateProductOffsets();

  const { amounts, types } = productData.reduce(
    (acc, product) => {
      acc.amounts.add(product.offset_amount);
      acc.types.add(product.offset_type);
      return acc;
    },
    {
      amounts: new Set<number>(),
      types: new Set<Offset["type"]>(),
    },
  );

  const sameInitialOffsets = amounts.size === 1 && types.size === 1;

  const initialOffset: Offset =
    sameInitialOffsets ?
      { amount: amounts.values().next().value!, type: types.values().next().value! }
    : {
        amount: 0,
        type: "percent",
      };

  const form = useFormily({ initialValues: { offset: initialOffset } });

  const productIds = useMemo(
    () =>
      productData
        .map((product) => product.id ?? product.product_id)
        .filter((id): id is number => typeof id === "number"),
    [productData],
  );

  const sortedProductData = useMemo(
    () =>
      [...productData].sort((a, b) => {
        const amountDifference = a.offset_amount - b.offset_amount;
        if (amountDifference !== 0) return amountDifference;
        if (a.offset_type !== b.offset_type) {
          return a.offset_type.localeCompare(b.offset_type);
        }
        return a.sku.localeCompare(b.sku);
      }),
    [productData],
  );

  return (
    <Modal
      isOpen={modal.visible}
      isDismissable
      onOpenChange={(isOpen) => (isOpen ? void 0 : modal.remove())}
      title={t(($) => $.pricingv2["Set Offset"])}
      css={{ width: "fit-content", height: "fit-content" }}
    >
      <FormProvider form={form}>
        <FormConsumer>
          {() => (
            <VStack alignItems={"center"} paddingX="medium">
              {!sameInitialOffsets && (
                <figure
                  className={css({
                    width: "100%",
                    fontSize: "medium",
                  })}
                >
                  <figcaption
                    className={css({
                      marginBottom: "small",
                      fontWeight: "semibold",
                    })}
                  >
                    {t(($) => $.pricingv2["Current offsets differ between selected products"])}
                  </figcaption>
                  <table
                    className={css({
                      width: "100%",
                      borderCollapse: "collapse",
                      "& th": {
                        borderBottom: "1px solid #e2e8f0",
                        paddingY: "2",
                        paddingX: "3",
                        fontWeight: "semibold",
                        textAlign: "left",
                      },
                      "& td": {
                        borderBottom: "1px solid #edf2f7",
                        paddingY: "2",
                        paddingX: "3",
                        whiteSpace: "nowrap",
                      },
                      "& tbody tr:last-of-type td": {
                        borderBottom: "none",
                      },
                    })}
                  >
                    <thead>
                      <tr>
                        <th>{t(($) => $.pricingv2["SKU"])}</th>
                        <th>{t(($) => $.pricingv2["ASIN"])}</th>
                        <th>{t(($) => $.pricingv2["Offset"])}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedProductData.map((product) => (
                        <tr key={product.id ?? product.product_id ?? product.sku}>
                          <td>{product.sku}</td>
                          <td>{product.asin ?? "—"}</td>
                          <td>
                            <OffsetDisplay
                              value={{
                                amount: product.offset_amount,
                                type: product.offset_type,
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </figure>
              )}
              <Field
                name={"offset"}
                title={t(($) => $.pricingv2["Set offset price for all selected products to"])}
                component={[OffsetInput, { css: css.raw({ maxWidth: "300px" }) }]}
                description={t(
                  ($) => $.pricingv2["Setting an offset can make the price go outside of min/max boundaries"],
                )}
              />
              <Button
                isPending={form.submitting}
                onPress={async () =>
                  await form.submit(async (values) => {
                    await updateOffsetMutation.mutateAsync({
                      plan_id: planId,
                      offset: values.offset,
                      product_ids: productIds,
                    });
                    modal.remove();
                  })
                }
              >
                {t(($) => $.common.actions["Save"])}
              </Button>
            </VStack>
          )}
        </FormConsumer>
      </FormProvider>
    </Modal>
  );
});
